import { Link as RouterLink } from "react-router-dom";
import {
  Copy,
  Check,
  BarChart3,
  Trash2,
  ExternalLink,
  QrCode,
  Pencil,
  CopyPlus,
  Lock,
  CalendarClock,
  AlertCircle,
  Megaphone,
  Globe,
  Star,
  Gauge,
  Smartphone,
  Shuffle,
} from "lucide-react";
import { Link as LinkType, Campaign } from "../../types";
import {
  buildRedirectUrl,
  copyToClipboard,
  formatDate,
  formatNumber,
  isExpired,
  isExpiringSoon,
} from "../../utils";
import { useState } from "react";
import Sparkline from "./Sparkline";
import Badge from "./Badge";
import { useMarketplace } from "../../marketplace/context";

interface LinkCardProps {
  link: LinkType;
  sparklineData?: number[];
  campaign?: Campaign | null; // optional, used to render a colored chip
  onEdit?: (link: LinkType) => void;
  onDuplicate?: (link: LinkType) => void;
  onDelete?: (link: LinkType) => void;
  onToggleFavorite?: (link: LinkType) => void;
}

const domainOf = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

const Favicon = ({ url }: { url: string }) => {
  const [error, setError] = useState(false);
  const domain = domainOf(url);
  if (error || !domain) {
    return (
      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-100 to-violet-100 text-brand-600 flex items-center justify-center font-display font-bold text-sm">
        {domain.charAt(0).toUpperCase() || "·"}
      </div>
    );
  }
  return (
    <div className="w-9 h-9 rounded-xl bg-white border border-ink-200/70 flex items-center justify-center shrink-0 overflow-hidden">
      <img
        src={`https://www.google.com/s2/favicons?sz=64&domain=${domain}`}
        alt=""
        className="w-5 h-5"
        onError={() => setError(true)}
      />
    </div>
  );
};

const LinkCard = ({
  link,
  sparklineData = [],
  campaign,
  onEdit,
  onDuplicate,
  onDelete,
  onToggleFavorite,
}: LinkCardProps) => {
  const { buildPath } = useMarketplace();
  const [copied, setCopied] = useState(false);
  const code = link.slug || link._id;
  const shortUrl = buildRedirectUrl(code, link.domain_host, link.append_contact_id);
  const shortPath = `/r/${link.slug || link._id.slice(-6)}`;
  const expired = isExpired(link.expires_at);
  const expiringSoon = isExpiringSoon(link.expires_at);
  const limitReached =
    typeof link.max_clicks === "number" && link.max_clicks > 0 && link.clicks >= link.max_clicks;

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const ok = await copyToClipboard(shortUrl);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const stop = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div
      className={`group relative bg-white border rounded-2xl shadow-card hover:shadow-card-hover transition-all ${
        expired
          ? "border-rose-200/70 bg-rose-50/30"
          : "border-ink-200/70 hover:border-ink-300/70"
      }`}
    >
      <div className="p-4 sm:p-5 flex items-start gap-4">
        <Favicon url={link.url} />

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-medium text-ink-900 truncate">
                  {link.title || "Untitled"}
                </h3>
                {campaign && (
                  <span
                    className="inline-flex items-center gap-1 px-1.5 h-5 rounded-md text-[10px] font-medium ring-1 ring-ink-200/60 bg-white"
                    title={`Campaign: ${campaign.name}`}
                  >
                    <Megaphone
                      size={9}
                      style={{ color: campaign.color }}
                    />
                    <span className="text-ink-700 truncate max-w-[120px]">
                      {campaign.name}
                    </span>
                  </span>
                )}
                {link.has_password && (
                  <Badge tone="emerald" icon={<Lock size={9} />}>
                    Password
                  </Badge>
                )}
                {link.geo_rules && link.geo_rules.length > 0 && (
                  <span
                    title={`Country targeting: ${link.geo_rules
                      .map((r) => r.country_code)
                      .join(", ")}`}
                  >
                    <Badge tone="brand" icon={<Globe size={9} />}>
                      {link.geo_rules.length}{" "}
                      {link.geo_rules.length === 1 ? "country" : "countries"}
                    </Badge>
                  </span>
                )}
                {link.device_rules && link.device_rules.length > 0 && (
                  <Badge tone="brand" icon={<Smartphone size={9} />}>
                    {link.device_rules.length}{" "}
                    {link.device_rules.length === 1 ? "device" : "devices"}
                  </Badge>
                )}
                {link.rotation && link.rotation.length > 0 && (
                  <Badge tone="brand" icon={<Shuffle size={9} />}>A/B {link.rotation.length}</Badge>
                )}
                {typeof link.max_clicks === "number" && link.max_clicks > 0 && (
                  <Badge tone={limitReached ? "rose" : "amber"} icon={<Gauge size={9} />}>
                    {formatNumber(link.clicks)}/{formatNumber(link.max_clicks)}
                  </Badge>
                )}
                {expired ? (
                  <Badge tone="rose" icon={<AlertCircle size={9} />}>
                    Expired
                  </Badge>
                ) : expiringSoon ? (
                  <Badge tone="amber" icon={<CalendarClock size={9} />}>
                    Expires soon
                  </Badge>
                ) : link.expires_at ? (
                  <Badge tone="neutral" icon={<CalendarClock size={9} />}>
                    Expires {formatDate(link.expires_at)}
                  </Badge>
                ) : null}
                {link.tags?.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center h-5 px-2 rounded-md bg-ink-100 text-ink-600 text-[11px] font-medium"
                  >
                    {t}
                  </span>
                ))}
              </div>
              <a
                href={link.url}
                target="_blank"
                rel="noreferrer"
                onClick={stop}
                className="inline-flex items-center gap-1 text-xs text-ink-500 hover:text-brand-600 truncate max-w-full mt-0.5"
              >
                <span className="truncate">{link.url}</span>
                <ExternalLink size={10} className="shrink-0" />
              </a>
            </div>
            <div className="text-right shrink-0">
              <div className="font-display font-semibold text-ink-900 text-lg tabular-nums leading-none">
                {formatNumber(link.clicks)}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-ink-400 mt-1">
                {link.clicks === 1 ? "click" : "clicks"}
              </div>
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={handleCopy}
                className="group/copy inline-flex items-center gap-1.5 h-7 pl-2 pr-2.5 rounded-lg bg-brand-50/60 hover:bg-brand-100/80 text-brand-700 text-xs font-mono font-medium transition-colors"
              >
                {copied ? (
                  <Check size={12} className="text-emerald-600" />
                ) : (
                  <Copy
                    size={12}
                    className="opacity-60 group-hover/copy:opacity-100"
                  />
                )}
                {copied ? "Copied" : shortPath}
              </button>
              <Badge tone="neutral" className="hidden sm:inline-flex">
                {formatDate(link.createdAt)}
              </Badge>
            </div>

            <div className="flex items-center gap-2">
              {sparklineData.length > 0 && (
                <div className="hidden sm:block opacity-80 group-hover:opacity-100 transition-opacity">
                  <Sparkline
                    data={sparklineData}
                    width={88}
                    height={28}
                    stroke="#6366f1"
                    showDot={false}
                  />
                </div>
              )}
              <div className="flex items-center gap-0.5">
                {onToggleFavorite && (
                  <button
                    onClick={(e) => {
                      stop(e);
                      onToggleFavorite(link);
                    }}
                    title={link.favorite ? "Unpin" : "Pin to top"}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                      link.favorite
                        ? "text-amber-500 hover:bg-amber-50"
                        : "text-ink-400 hover:bg-ink-100 hover:text-amber-500"
                    }`}
                  >
                    <Star size={14} fill={link.favorite ? "currentColor" : "none"} />
                  </button>
                )}
                <RouterLink
                  to={buildPath(`qr?link=${link._id}`)}
                  title="Generate QR"
                  className="w-8 h-8 rounded-lg hover:bg-ink-100 text-ink-500 hover:text-ink-900 flex items-center justify-center transition-all"
                >
                  <QrCode size={14} />
                </RouterLink>
                <RouterLink
                  to={buildPath(`analytics/${link._id}`)}
                  title="View analytics"
                  className="w-8 h-8 rounded-lg hover:bg-ink-100 text-ink-500 hover:text-ink-900 flex items-center justify-center transition-all"
                >
                  <BarChart3 size={14} />
                </RouterLink>
                {onEdit && (
                  <button
                    onClick={(e) => {
                      stop(e);
                      onEdit(link);
                    }}
                    title="Edit"
                    className="w-8 h-8 rounded-lg hover:bg-ink-100 text-ink-500 hover:text-ink-900 flex items-center justify-center transition-all"
                  >
                    <Pencil size={14} />
                  </button>
                )}
                {onDuplicate && (
                  <button
                    onClick={(e) => {
                      stop(e);
                      onDuplicate(link);
                    }}
                    title="Duplicate"
                    className="w-8 h-8 rounded-lg hover:bg-ink-100 text-ink-500 hover:text-ink-900 flex items-center justify-center transition-all"
                  >
                    <CopyPlus size={14} />
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={(e) => {
                      stop(e);
                      onDelete(link);
                    }}
                    title="Delete"
                    className="w-8 h-8 rounded-lg hover:bg-rose-50 text-ink-500 hover:text-rose-600 flex items-center justify-center transition-all"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkCard;
